import axios from '@axios'

const BASE = 'admin/notifications'

/**
 * Admin notification bell endpoints.
 *
 * Collected here for the same reason as contactService: the bell and its full
 * page share every one of these calls, so each URL has a single definition.
 */
export default {
  list(params) {
    return axios.get(BASE, { params })
  },
  unreadCount() {
    return axios.get(`${BASE}/unread-count`)
  },
  markRead(ids) {
    return axios.post(`${BASE}/read`, { notification_ids: ids })
  },
  markAllRead() {
    return axios.post(`${BASE}/read-all`)
  },
  // DELETE carries its body under `data` in axios.
  remove(ids) {
    return axios.delete(BASE, { data: { notification_ids: ids } })
  },
  removeAll() {
    return axios.delete(`${BASE}/all`)
  },
}

// Mirrors App\Enums\AdminNotificationType on the backend.
export const NOTIFICATION_TYPES = {
  CONTACT_NEW: 'contact_new',
  CONTACT_REPLY: 'contact_reply',
}

export const TYPE_OPTIONS = [
  { value: '', text: 'All Types' },
  { value: NOTIFICATION_TYPES.CONTACT_NEW, text: 'New Contact Message' },
  { value: NOTIFICATION_TYPES.CONTACT_REPLY, text: 'Contact Reply' },
]

const DISPLAY = {
  [NOTIFICATION_TYPES.CONTACT_NEW]: { icon: 'MailIcon', variant: 'light-primary' },
  [NOTIFICATION_TYPES.CONTACT_REPLY]: { icon: 'CornerUpLeftIcon', variant: 'light-info' },
}

// An unknown type must still render — the backend can add one before the panel
// knows about it, and a blank row would read as a bug.
const FALLBACK = { icon: 'BellIcon', variant: 'light-secondary' }

export function notificationIcon(notification) {
  return (DISPLAY[notification.type] || FALLBACK).icon
}

export function notificationVariant(notification) {
  return (DISPLAY[notification.type] || FALLBACK).variant
}

/**
 * Where clicking the notification lands. Contact rows carry their thread id in
 * `meta`, put there when the row was written, so the bell opens the
 * conversation without first fetching it.
 */
export function notificationRoute(notification) {
  const meta = notification.meta || {}

  if (notification.type === NOTIFICATION_TYPES.CONTACT_NEW
    || notification.type === NOTIFICATION_TYPES.CONTACT_REPLY) {
    return meta.thread_id
      ? { name: 'view-contact-thread', params: { threadId: String(meta.thread_id) } }
      : null
  }

  return null
}

/**
 * Short relative wording for anything recent, absolute once it stops being
 * "today" — the bell is read at a glance, the page is read carefully.
 */
export function formatNotificationDate(value) {
  if (!value) return ''

  // Safari will not parse 'YYYY-MM-DD HH:MM:SS'; the API sends exactly that.
  const date = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`

  return date.toLocaleString()
}
